import { useLayoutEffect, useRef, type HTMLAttributes, type PointerEvent, type ReactNode, type TableHTMLAttributes, type ThHTMLAttributes } from "react";
import { cx } from "./cx";
import { Spinner } from "./Spinner";
import { resetWidth, restoreWidths, startResize } from "./colResize";
import { colWidth, type ColWidth } from "./widths";

/** 表格容器：边框、圆角、横向滚动。空数据时在里面放 EmptyState。
 * loading 时盖一层转圈遮罩（延迟淡入，快的请求不闪），没有行时撑出一块空白放转圈；只在首次加载与换条件时传，后台轮询不传（#541，决策 D199）。 */
export function TableWrap({ className, loading, children, ...rest }: HTMLAttributes<HTMLDivElement> & { loading?: boolean }) {
  return (
    <div className={cx("ax-table-wrap", loading && "is-loading", className)} aria-busy={loading || undefined} {...rest}>
      {children}
      {loading && <div className="ax-table-loading"><Spinner label="加载中" /><span aria-hidden>加载中</span></div>}
    </div>
  );
}

/** 单元格或表头被省略号截断时，悬停补上全文 title（打 data-auto-title 标记，每次悬停按当前内容重写，不再截断就去掉）；调用方给的 title 不动。 */
function titleOverflow(e: PointerEvent<HTMLTableElement>) {
  const cell = (e.target as HTMLElement).closest("td, th");
  if (!(cell instanceof HTMLElement) || (cell.title && cell.dataset.autoTitle === undefined)) return;
  if (cell.scrollWidth > cell.clientWidth) {
    cell.title = cell.textContent ?? "";
    cell.dataset.autoTitle = "";
  } else if (cell.dataset.autoTitle !== undefined) {
    cell.removeAttribute("title");
    delete cell.dataset.autoTitle;
  }
}

/** 最后一列内容至少要的宽度（含左右内边距）：取各行最后一格里内容的实际宽度，封顶 LAST_MAX。 */
const LAST_MAX = 360;
function lastNeed(table: HTMLTableElement, columns: number) {
  const range = document.createRange();
  if (typeof range.getBoundingClientRect !== "function") return 0; // jsdom 没有排版，不量
  let need = 0;
  for (const row of table.rows) {
    const cell = row.cells[row.cells.length - 1];
    if (row.cells.length !== columns || !cell?.firstChild) continue;
    range.selectNodeContents(cell);
    const pad = parseFloat(getComputedStyle(cell).paddingLeft) + parseFloat(getComputedStyle(cell).paddingRight);
    need = Math.max(need, range.getBoundingClientRect().width + pad);
  }
  return Math.min(Math.ceil(need), LAST_MAX);
}

/** 固定布局下，各列之和超过容器时最后一列（不给宽度的那列）会被压成 0：把表格的最小宽度设为其余列之和加最后一列内容的宽度，放不下就横向滚动。
 * 不能改用 width: max-content，那样浏览器不认 table-layout: fixed，列宽又会随内容变。 */
function keepLast(table: HTMLTableElement) {
  const row = table.tBodies[0]?.rows[0] ?? table.tHead?.rows[table.tHead.rows.length - 1];
  if (!row || row.cells.length < 2) return;
  const cells = [...row.cells];
  const fixed = cells.slice(0, -1).reduce((sum, c) => sum + c.getBoundingClientRect().width, 0);
  const min = `${Math.ceil(fixed) + lastNeed(table, cells.length)}px`;
  if (table.style.minWidth !== min) table.style.minWidth = min;
}

/**
 * 表格：固定列宽（table-layout: fixed），列宽由各 Th 的 w 给出，最后一列不给宽度、吸收剩余，列宽不随内容跳动；
 * 表头有两行（分组表头）时第一行跨列，改用 cols 逐列给宽度（#541，决策 D199）。放不下的内容用省略号截断，悬停看全文。
 * 各列之和超过容器时整表横向滚动，最后一列保留自己内容的宽度（keepLast）。
 * 除最后一列与分组表头外，拖表头右边缘可调列宽，双击边缘恢复默认，调过的宽度按页面与表格记在浏览器里（colResize）。
 * 单元格上下内边距按行高与 20px 行高算出（--row-h 时 8px，dense 时 6px），单行文字仍是一行高，多行内容的上下留白与单行一致；
 * striped 是斑马纹：偶数行浅底色、不画行间分隔线，行多又密的表格（消息记录）用，读起来不刺眼。
 * 单元格内容垂直居中。表格里的小号按钮（ax-btn-sm）收回多出的高度，不撑高行。
 */
export function Table({ dense, striped, cols, className, children, ...rest }: TableHTMLAttributes<HTMLTableElement> & { dense?: boolean; striped?: boolean; cols?: (ColWidth | undefined)[] }) {
  const ref = useRef<HTMLTableElement>(null);
  // 每次渲染后重算：显示列、行内容变了都可能改变所需的最小宽度；只读第一行与各行最后一格的尺寸，一页几十行开销很小
  const fit = () => { if (ref.current) keepLast(ref.current); };
  useLayoutEffect(() => {
    if (!ref.current) return;
    restoreWidths(ref.current);
    keepLast(ref.current);
  });
  return (
    <table ref={ref} className={cx("ax-table", dense && "ax-table-dense", striped && "ax-table-striped", className)} onPointerOver={titleOverflow}
      onPointerDown={(e) => startResize(e, fit)} onDoubleClick={(e) => resetWidth(e, fit)} {...rest}>
      {cols && <colgroup>{cols.map((w, i) => <col key={i} style={w === undefined ? undefined : { width: colWidth(w) }} />)}</colgroup>}
      {children}
    </table>
  );
}

export interface ThProps extends ThHTMLAttributes<HTMLTableCellElement> {
  numeric?: boolean;
  sortable?: boolean;
  sorted?: "asc" | "desc" | false;
  /** 列宽（col-w-* 令牌或像素）；最后一列不给，吸收剩余宽度。 */
  w?: ColWidth;
}

export function Th({ numeric, sortable, sorted, w, className, style, ...rest }: ThProps) {
  return <th className={cx(numeric && "ax-num", sortable && "is-sortable", sorted && "is-sorted", sorted === "asc" && "asc", className)} style={w === undefined ? style : { width: colWidth(w), ...style }} aria-sort={sorted ? (sorted === "asc" ? "ascending" : "descending") : undefined} {...rest} />;
}

/** 表格底部：左边每页行数与条数说明（横排、同一间距），右边分页。 */
export function TableFooter({ left, right }: { left: ReactNode; right?: ReactNode }) {
  return (
    <div className="ax-table-footer">
      <span>{left}</span>
      {right}
    </div>
  );
}
